import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, apiError } from "../context/AuthContext";
import { ArrowRight, Loader2, ShieldCheck, RefreshCw, MailCheck } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
// THIS BREAKS THE AUTH.
function startGoogleSignIn() {
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

const BG = "https://images.unsplash.com/photo-1583847268964-b28dc8f51f92?w=1600&q=80&auto=format&fit=crop";

export default function Login() {
  const { login, verifyOtp, resendOtp } = useAuth();
  const navigate = useNavigate();

  // Two-step form: 'password' -> 'otp'.
  const [step, setStep] = useState("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [trustDevice, setTrustDevice] = useState(true);
  const [otpSession, setOtpSession] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [expiresAt, setExpiresAt] = useState(0);
  const [now, setNow] = useState(Date.now());
  const [sentAt, setSentAt] = useState(0);
  const [resentAt, setResentAt] = useState(0);

  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);
  const codeInputRef = useRef(null);

  useEffect(() => {
    if (step === "otp" && codeInputRef.current) codeInputRef.current.focus();
  }, [step]);

  // Ticking clock for the countdown on the OTP screen. Only runs while
  // the OTP step is open to avoid a background timer on the login screen.
  useEffect(() => {
    if (step !== "otp" || !expiresAt) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [step, expiresAt]);

  const remainingMs = Math.max(0, expiresAt - now);
  const remainingMin = Math.floor(remainingMs / 60_000);
  const remainingSec = Math.floor((remainingMs % 60_000) / 1000);
  const remaining = `${remainingMin}:${String(remainingSec).padStart(2, "0")}`;
  const expired = expiresAt && remainingMs === 0;
  const justSent = step === "otp" && sentAt && Date.now() - sentAt < 6000;

  const submitPassword = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");
    setBusy(true);
    try {
      const result = await login(email.trim(), password);
      // Trusted-device path returns a user object; otherwise a payload with otp_required.
      if (result && result.otp_required) {
        setOtpSession(result.otp_session);
        setMaskedEmail(result.masked_email || email);
        const mins = result.expires_in_minutes || 5;
        setExpiresAt(Date.now() + mins * 60_000);
        setNow(Date.now());
        setSentAt(Date.now());
        setStep("otp");
        setCode("");
      } else {
        navigate("/");
      }
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");
    setBusy(true);
    try {
      await verifyOtp(otpSession, code.trim(), trustDevice);
      navigate("/");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || "Verification failed");
    } finally {
      setBusy(false);
    }
  };

  const handleResend = async () => {
    setError("");
    setInfo("");
    // Simple client-side cooldown so users can't spam the button.
    if (Date.now() - resentAt < 20_000) {
      setInfo("Please wait a few seconds before requesting another code.");
      return;
    }
    setBusy(true);
    try {
      const r = await resendOtp(otpSession);
      setOtpSession(r.otp_session || otpSession);
      setMaskedEmail(r.masked_email || maskedEmail);
      const mins = r.expires_in_minutes || 5;
      setExpiresAt(Date.now() + mins * 60_000);
      setNow(Date.now());
      setResentAt(Date.now());
      setSentAt(Date.now());
      setInfo("A new code was sent to your email.");
      setCode("");
      codeInputRef.current?.focus();
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || "Could not resend code");
    } finally {
      setBusy(false);
    }
  };

  const backToPassword = () => {
    setStep("password");
    setOtpSession("");
    setCode("");
    setError("");
    setInfo("");
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <img src={BG} alt="UNWIND bespoke sofa" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/45" />
        <div className="relative z-10 flex flex-col justify-between p-14 text-white">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-lg bg-white flex items-center justify-center p-1.5">
              <img src="/unwind-mark.png" alt="UNWIND" className="w-full h-full object-contain" />
            </div>
            <div className="leading-tight">
              <span className="font-heading text-xl tracking-[0.15em]">UNWIND</span>
              <p className="text-[10px] tracking-[0.2em] uppercase text-stone-200 mt-0.5">Furniture Company</p>
            </div>
          </div>
          <div>
            <h1 className="font-heading text-5xl font-light leading-tight tracking-tight">
              Never lose a<br />lead again.
            </h1>
            <p className="mt-5 text-stone-200 max-w-md leading-relaxed">
              Track ownership, follow-ups and outcomes for every bespoke enquiry — from first
              WhatsApp message to closed order.
            </p>
          </div>
          <p className="text-xs text-stone-300 tracking-[0.2em] uppercase">Bespoke Furniture · Bangalore</p>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center px-6 bg-[#FAFAF9]">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <div className="h-12 w-12 rounded-lg bg-stone-100 flex items-center justify-center p-1.5">
              <img src="/unwind-mark.png" alt="UNWIND" className="w-full h-full object-contain" />
            </div>
            <div className="leading-tight">
              <span className="font-heading text-xl tracking-[0.15em] text-stone-900">UNWIND</span>
              <p className="text-[10px] tracking-[0.2em] uppercase text-stone-500 mt-0.5">Furniture Company</p>
            </div>
          </div>

          {step === "password" && (
            <>
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-stone-500">Leads CRM</p>
              <h2 className="font-heading text-3xl font-normal tracking-tight text-stone-900 mt-2">Welcome back</h2>
              <p className="text-stone-500 mt-2 text-sm">Sign in to work your pipeline.</p>

              <button
                type="button"
                onClick={startGoogleSignIn}
                data-testid="login-google"
                className="mt-8 w-full flex items-center justify-center gap-3 border border-stone-300 hover:border-stone-500 hover:bg-stone-50 text-stone-900 rounded-md py-2.5 font-medium transition-colors bg-white"
              >
                {/* Inline Google 'G' SVG — avoids pulling in an image asset. */}
                <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                  <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                  <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                  <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                  <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                </svg>
                Sign in with Google
              </button>

              <div className="flex items-center gap-3 my-6 text-xs uppercase tracking-[0.2em] text-stone-400">
                <div className="flex-1 border-t border-stone-200" />
                or
                <div className="flex-1 border-t border-stone-200" />
              </div>

              <form onSubmit={submitPassword} className="space-y-4" data-testid="login-form">
                <div>
                  <label className="text-xs tracking-[0.15em] uppercase font-semibold text-stone-500">Email</label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="login-email"
                    className="mt-1.5 w-full border border-stone-300 rounded-md px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/70"
                    placeholder="you@unwindfurniture.com"
                  />
                </div>
                <div>
                  <label className="text-xs tracking-[0.15em] uppercase font-semibold text-stone-500">Password</label>
                  <input
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="login-password"
                    className="mt-1.5 w-full border border-stone-300 rounded-md px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/70"
                    placeholder="••••••••"
                  />
                </div>
                {error && (
                  <p className="text-sm text-red-600" data-testid="login-error">{error}</p>
                )}
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="login-submit"
                  className="w-full bg-stone-900 hover:bg-stone-800 text-white rounded-md py-2.5 font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Continue <ArrowRight className="h-4 w-4" /></>}
                </button>
              </form>
              <p className="text-xs text-stone-400 mt-8 leading-relaxed">
                Accounts are created by your admin. Enquiry data is used only to qualify and route leads (DPDP compliant).
              </p>
            </>
          )}

          {step === "otp" && (
            <>
              <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-semibold text-stone-500">
                <ShieldCheck className="h-3.5 w-3.5" /> Verification
              </div>
              <h2 className="font-heading text-3xl font-normal tracking-tight text-stone-900 mt-2" data-testid="otp-heading">
                Enter your code
              </h2>
              <p className="text-stone-500 mt-2 text-sm">
                We sent a 6-digit code to <span className="font-medium text-stone-700" data-testid="otp-masked-email">{maskedEmail}</span>.
              </p>

              {/* "Code sent" confirmation banner — auto-hides after a few seconds. */}
              {justSent && (
                <div
                  className="mt-4 flex items-start gap-2.5 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm text-emerald-800"
                  data-testid="otp-sent-banner"
                >
                  <MailCheck className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <div className="leading-snug">
                    <p className="font-medium">Code sent — check your inbox.</p>
                    <p className="text-emerald-700 text-xs mt-0.5">
                      Can&apos;t see it? Check the <span className="font-medium">spam / promotions</span> folder — the sender is <span className="font-mono">onboarding@resend.dev</span>.
                    </p>
                  </div>
                </div>
              )}

              <form onSubmit={submitOtp} className="mt-6 space-y-4" data-testid="otp-form">
                <div>
                  <div className="flex items-center justify-between">
                    <label className="text-xs tracking-[0.15em] uppercase font-semibold text-stone-500">6-digit code</label>
                    {expiresAt > 0 && (
                      <span
                        className={`text-xs font-mono tabular-nums ${expired ? "text-red-600" : "text-stone-500"}`}
                        data-testid="otp-countdown"
                      >
                        {expired ? "Expired" : `Expires in ${remaining}`}
                      </span>
                    )}
                  </div>
                  <input
                    ref={codeInputRef}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    pattern="[0-9]{6}"
                    required
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    data-testid="otp-code"
                    className="mt-1.5 w-full border border-stone-300 rounded-md px-3 py-3 text-lg tracking-[0.5em] text-center font-mono bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/70"
                    placeholder="••••••"
                  />
                </div>
                <label className="flex items-center gap-2 text-sm text-stone-600 select-none">
                  <input
                    type="checkbox"
                    checked={trustDevice}
                    onChange={(e) => setTrustDevice(e.target.checked)}
                    data-testid="otp-trust-device"
                    className="rounded border-stone-400"
                  />
                  Trust this device for 30 days
                </label>
                {info && <p className="text-sm text-stone-500" data-testid="otp-info">{info}</p>}
                {error && <p className="text-sm text-red-600" data-testid="otp-error">{error}</p>}
                <button
                  type="submit"
                  disabled={busy || code.length !== 6 || expired}
                  data-testid="otp-submit"
                  className="w-full bg-stone-900 hover:bg-stone-800 text-white rounded-md py-2.5 font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Verify & Sign in <ArrowRight className="h-4 w-4" /></>}
                </button>
              </form>

              <div className="flex items-center justify-between mt-6 text-xs">
                <button
                  type="button"
                  onClick={backToPassword}
                  data-testid="otp-back"
                  className="text-stone-500 hover:text-stone-900 tracking-[0.05em]"
                >
                  ← Use a different account
                </button>
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={busy}
                  data-testid="otp-resend"
                  className="text-stone-700 hover:text-stone-900 flex items-center gap-1.5 disabled:opacity-50"
                >
                  <RefreshCw className="h-3 w-3" /> Resend code
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
